import React from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useLayout } from '../theme/useLayout';
import { useAppSelector } from '../redux/hooks';
import { isAdminRole } from '../redux/slices/authSlice';
import ThemeToggle from '../components/ui/ThemeToggle';
import BrandMark from './BrandMark';
import UserMenu from './UserMenu';
import { MainTabParamList, NavItem, visibleNavItems } from './navItems';
import type { RootStackParamList } from './AppNavigator';

type TopNavProps = {
  /** Section currently displayed (undefined on detail screens). */
  activeName?: keyof MainTabParamList;
};

/**
 * Desktop header: logo, Airbnb-like icon category bar (underlined active tab), theme toggle
 * and account pill.
 */
export default function TopNav({ activeName }: TopNavProps) {
  const styles = useStyles();
  const { isWide, gutter } = useLayout();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const isAdmin = useAppSelector((s) => isAdminRole(s.auth.role));
  const items = visibleNavItems(isAdmin);

  const go = (name: keyof MainTabParamList) => {
    navigation.navigate('Main', { screen: name });
  };

  return (
    <View style={[styles.bar, { paddingHorizontal: gutter }]}>
      <View style={styles.brand}>
        <BrandMark showName={isWide} onPress={() => go('EcranAccueil')} />
      </View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.tabsScroll}
        contentContainerStyle={styles.tabs}
      >
        {items.map((item) => (
          <TopNavTab
            key={item.name}
            item={item}
            active={item.name === activeName}
            compact={!isWide}
            onPress={() => go(item.name)}
          />
        ))}
      </ScrollView>
      <View style={styles.actions}>
        <ThemeToggle variant="ghost" />
        <UserMenu />
      </View>
    </View>
  );
}

type TopNavTabProps = { item: NavItem; active: boolean; compact: boolean; onPress: () => void };

function TopNavTab({ item, active, compact, onPress }: TopNavTabProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="tab"
      accessibilityState={{ selected: active }}
      accessibilityLabel={item.label}
      style={[styles.tab, compact && styles.tabCompact]}
    >
      {(state) => {
        const hovered = (state as { hovered?: boolean }).hovered;
        const color = active || hovered ? colors.textPrimary : colors.textSecondary;
        return (
          <>
            <MaterialCommunityIcons name={active ? item.activeIcon : item.icon} size={22} color={color} />
            <Text style={[styles.tabLabel, { color }, active && styles.tabLabelActive]} numberOfLines={1}>
              {item.label}
            </Text>
            <View
              style={[
                styles.underline,
                active ? styles.underlineActive : hovered ? styles.underlineHover : null,
              ]}
            />
          </>
        );
      }}
    </Pressable>
  );
}

const useStyles = makeStyles((c) => ({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    height: 76,
    backgroundColor: c.background,
    borderBottomWidth: 1,
    borderBottomColor: c.divider,
  },
  brand: { flexShrink: 0 },
  tabsScroll: { flex: 1 },
  tabs: { flexGrow: 1, justifyContent: 'center', alignItems: 'stretch', gap: 4 },
  tab: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
    paddingTop: 12,
    minWidth: 76,
  },
  tabCompact: { paddingHorizontal: 8, minWidth: 60 },
  tabLabel: { fontSize: 12, fontWeight: '600', marginTop: 4 },
  tabLabelActive: { fontWeight: '700' },
  underline: { height: 2, alignSelf: 'stretch', marginTop: 10, borderRadius: 1, backgroundColor: 'transparent' },
  underlineActive: { backgroundColor: c.ink },
  underlineHover: { backgroundColor: c.border },
  actions: { flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 0 },
}));
